/**
 * BaseGridView.tsx - BaseGridView - Unified AG Grid wrapper
 * 
 * CHANGE HISTORY:
 * - 251101_0915 [pending] danielbeer: Added AG Grid module registration
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule, themeQuartz } from 'ag-grid-community';
import { useSkinnr } from '@opcentral/skinnr';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

export const BaseGridView = ({
  // Data
  data = [],
  columns = [],
  
  // Features
  pagination = true,
  pageSize = 50,
  filtering = true,
  sorting = true,
  editable = false,
  rowMode = true, // When true, rows are single objects (no cell focus/edit)
  
  // Selection - disabled by default to prevent accidental selection
  rowSelection = false as any,
  onSelectionChange,
  
  // Editing
  onCellEdit,
  onRowEdit,
  
  // Callbacks
  onRowClick,
  onCellClick,
  onCellContextMenu,
  onGridReady,
  
  // Styling
  skinId = 'grid-light',
  height = '100%',
  
  // Advanced
  gridOptions = {},
  
  // Other props
  ...otherProps
}) => {
  const gridRef = useRef(null);
  const [gridApi, setGridApi] = useState(null);
  
  // Temporarily bypass Skinnr to avoid type issues
  // const { getSkin, getCurrentMode } = useSkinnr();
  
  // Get skin configuration
  const skin = useMemo(() => {
    // Temporarily return null to use default AG Grid theme
    return null;
    // if (!skinId) {
    //   // Auto-detect based on mode
    //   const mode = getCurrentMode();
    //   return getSkin(mode === 'dark' ? 'grid-dark' : 'grid-light');
    // }
    // return getSkin(skinId);
  }, [skinId]);
  
  // Apply skin CSS variables
  const gridStyle = useMemo(() => {
    const cssVars = {};
    if (skin?.variables) {
      Object.entries(skin.variables).forEach(([key, value]) => {
        cssVars[`--${key}`] = value;
      });
    }
    
    return {
      height,
      width: '100%',
      ...cssVars
    };
  }, [skin, height]);
  
  // Default column definition
  const defaultColDef = useMemo(() => ({
    sortable: sorting,
    filter: filtering,
    resizable: true,
    editable: rowMode ? false : editable, // Disable editing in row mode
    suppressNavigable: rowMode, // Prevent cell focus in row mode
    flex: 1,
    minWidth: 100,
  }), [sorting, filtering, editable, rowMode]);
  
  // Grid ready callback
  const handleGridReady = useCallback((params) => {
    setGridApi(params.api);
    if (onGridReady) {
      onGridReady(params);
    }
  }, [onGridReady]);
  
  // Cell value changed
  const handleCellValueChanged = useCallback((params) => {
    if (onCellEdit) {
      onCellEdit(params);
    }
  }, [onCellEdit]);
  
  // Row clicked
  const handleRowClicked = useCallback((params) => {
    if (onRowClick) {
      onRowClick(params);
    }
  }, [onRowClick]);
  
  // Cell clicked
  const handleCellClicked = useCallback((params) => {
    if (onCellClick) {
      onCellClick(params);
    }
  }, [onCellClick]);
  
  // Selection changed
  const handleSelectionChanged = useCallback(() => {
    if (onSelectionChange && gridApi) {
      const selectedRows = gridApi.getSelectedRows();
      onSelectionChange(selectedRows);
    }
  }, [onSelectionChange, gridApi]);
  
  // Use v34 theme system
  const gridTheme = useMemo(() => themeQuartz, []);
  
  return (
    <div 
      className="base-grid-view"
      style={gridStyle}
    >
      <AgGridReact
        theme={gridTheme}
        ref={gridRef}
        
        // Data
        rowData={data}
        columnDefs={columns}
        
        // Column defaults
        defaultColDef={defaultColDef}
        
        // Pagination
        pagination={pagination}
        paginationPageSize={pageSize || 50}
        paginationPageSizeSelector={[10, 25, 50, 100, 200]}
        
        // Selection - use v34 object syntax, disable by default
        rowSelection={rowSelection === false ? undefined : (typeof rowSelection === 'string' ? { mode: rowSelection === 'single' ? 'singleRow' : 'multiRow' } : rowSelection)}
        
        // Editing
        editType="fullRow"
        stopEditingWhenCellsLoseFocus={true}
        suppressClickEdit={rowMode} // Prevent click-to-edit in row mode
        
        // Callbacks
        onGridReady={handleGridReady}
        onCellValueChanged={handleCellValueChanged}
        onRowClicked={handleRowClicked}
        onCellClicked={handleCellClicked}
        onCellContextMenu={onCellContextMenu}
        onSelectionChanged={handleSelectionChanged}
        
        // Advanced options
        {...gridOptions}
        {...otherProps}
      />
    </div>
  );
};

export default BaseGridView;
